import { Trans } from '@lingui/react/macro';
import { Link2 } from 'lucide-react';
import TelegramIntegrationCard from 'modules/integrations/ui/components/telegram-integration-card';
import { useTelegramBotInfoQuery, useTelegramIntegrationQuery } from 'modules/integrations/store/tournament-integrations-api';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import React, { useMemo } from 'react';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDCard from 'ud-ui/components/card';
import UDText from 'ud-ui/components/text';

/**
 * Вкладка настроек «Интеграции»: соцсети, в которые организация публикует посты от имени
 * бота ImGame. Сейчас — только Telegram, одна интеграция на организацию.
 */
const IntegrationsTab = () => {
  const { info, isLoaded: isOrganizerLoaded } = useOrganizer();
  const organizerId = info?.id ?? 0;
  // Вкладки настроек открываются и без организации (`tournamentRequired: false`) — тогда
  // запрашивать нечего, и вечный спиннер вместо объяснения был бы враньём.
  const hasNoOrganizer = isOrganizerLoaded && !organizerId;

  const { data: botInfo } = useTelegramBotInfoQuery();
  const {
    data: integration,
    currentData,
    isFetching,
    isError,
  } = useTelegramIntegrationQuery(organizerId, {
    skip: !organizerId,
  });

  // `null` — тоже готовый ответ («канал не подключён»), поэтому «пусто» здесь не бывает.
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: currentData !== undefined ? 1 : 0,
        hasCurrentData: currentData !== undefined,
        isFetching,
        isError,
      }),
    [currentData, isFetching, isError],
  );

  return (
    <div className="flex max-w-xl flex-col gap-4" data-testid="integrations-tab">
      <UDCard accent="attention" className="flex items-center gap-3">
        <span className="bg-surfaceSunken text-darkYellow flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
          <Link2 size={18} aria-hidden />
        </span>
        <UDText type="footnote" className="text-inkSecondary">
          <Trans>Подключите соцсети, чтобы публиковать анонсы и результаты матчей от имени организатора.</Trans>
        </UDText>
      </UDCard>

      {hasNoOrganizer ? (
        <UDText type="subhead" className="text-inkSecondary" data-testid="integrations-tab-no-organizer">
          <Trans>Интеграции подключаются к организации — сначала создайте или выберите её.</Trans>
        </UDText>
      ) : (
        <UDAsyncContent
          state={state}
          minHeight={120}
          error={
            <UDText type="subhead" className="text-inkSecondary p-4" data-testid="integrations-tab-error">
              <Trans>Не удалось загрузить интеграции. Обновите страницу.</Trans>
            </UDText>
          }
        >
          <TelegramIntegrationCard organizerId={organizerId} integration={integration ?? null} botInfo={botInfo} />
        </UDAsyncContent>
      )}
    </div>
  );
};

export default IntegrationsTab;
